import type { AssistantPlan } from "./assistantAccess"

export type AssistantRequestMode =
  | "general"
  | "build_with_ai"
  | "trade_analysis"
  | "trade_discovery"
  | "analyze_matches"

export type AssistantToolName =
  | "web_search"
  | "file_search"
  | "get_selected_trade_context"
  | "analyze_trade_setup"
  | "discover_trade_setups"
  | "analyze_matches"

export function normalizeAssistantRequestMode(
  value?: string | null,
): AssistantRequestMode {
  return value === "build_with_ai" ||
    value === "trade_analysis" ||
    value === "trade_discovery" ||
    value === "analyze_matches"
    ? value
    : "general"
}

export function buildAssistantPromptCacheKey(args: {
  plan: AssistantPlan
  requestMode: AssistantRequestMode
  locale: string
}) {
  return `oddsfantasy-assistant-turn:${args.plan}:${args.requestMode}:${args.locale}:v1`
}

export function shouldIncludeDetailedTradeContext(
  requestMode: AssistantRequestMode,
) {
  return requestMode === "build_with_ai" || requestMode === "trade_analysis"
}

export function shouldIncludeBuildWithAiTradeContext(
  requestMode: AssistantRequestMode,
) {
  return requestMode === "build_with_ai"
}

export function shouldAnalyzeSelectedMatches(
  question: string,
  hasSelectedMatches: boolean,
) {
  if (!hasSelectedMatches) return false

  return /\b(?:these|selected|chosen|checked|my)\s+(?:matches|games|events)\b|\b(?:matches|games|events)\s+(?:i|we)\s+(?:selected|chose|checked)\b|\bwhich\s+of\s+(?:these|them)\b/i.test(
    String(question ?? ""),
  )
}

export function getAssistantToolNamesForRequest(args: {
  plan: AssistantPlan
  requestMode: AssistantRequestMode
  hasSelectedTradeContext: boolean
  hasSelectedMatches: boolean
  hasFileSearch: boolean
}) {
  const toolNames: AssistantToolName[] = []
  const canUsePaidWebSearch = args.plan !== "free"
  const allowWebSearch =
    args.requestMode === "general" ||
    args.requestMode === "build_with_ai"
  const allowFileSearch =
    args.requestMode === "general" || args.requestMode === "build_with_ai"

  if (allowWebSearch && canUsePaidWebSearch) {
    toolNames.push("web_search")
  }

  if (allowFileSearch && args.hasFileSearch) {
    toolNames.push("file_search")
  }

  if (
    args.requestMode === "build_with_ai" ||
    args.requestMode === "trade_analysis" ||
    (args.requestMode === "general" && args.hasSelectedTradeContext)
  ) {
    toolNames.push("get_selected_trade_context")
  }

  if (args.requestMode === "trade_analysis") {
    toolNames.push("analyze_trade_setup")
  }

  if (args.requestMode === "trade_discovery") {
    toolNames.push("discover_trade_setups")
  }

  if (
    args.hasSelectedMatches &&
    (args.requestMode === "general" || args.requestMode === "analyze_matches")
  ) {
    toolNames.push("analyze_matches")
  }

  return toolNames
}
